import React, { Fragment, useEffect, useState } from 'react'
import { Box, Divider, Typography } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import { Modal } from '../../../components/_EOS/Modal'
import { ModalReturnType } from '../../../hooks/useModal'
import { GenericTableV2 } from '../../../components/_EOS/GenericTable/GenericTableV2'
import { AutoCompleterV3, AutoCompleterConfig } from '../../../components/_WMS/AutoCompleterV3'
import { useApi } from '../../../hooks/useApi'
import { usePagination } from '../../../hooks/usePagination'
import { useSnackbar } from '../../../hooks/useSnackbar'
import { SnackBar } from '../../../components/SnackBar/Snackbar'
import { Asn } from '../../../interfaces/Asn/Asn'
import { User } from '../../../interfaces/User/User'
import { ApiConfig, apiCall } from '../../../config/api'
import { ApiResponse } from '../../../typings/ApiResponse'
import { Status } from '../../../hooks/useFetchState'
import { createColumns } from '../../../components/_EOS/GenericTable/helpers/configHelpers'

interface AsnDetailLpn {
  id: number
  asnId: number
  lpn: string
  userId: number | null
  userAlias: string | null
  lpnStateId: number | null
}

const userConfig: AutoCompleterConfig<User> = {
  searchEndpoint: `${ApiConfig.UserApi}/searchByName`,
  searchParam: 'searchText',
  keyField: 'userId',
  displayField: 'userName',
  minSearchLength: 3,
  debounceMs: 300,
  pageSize: 10
}

const lpnColumns = createColumns<AsnDetailLpn>([
  { key: 'lpn', title: 'LPN' },
  { key: 'userAlias', title: 'Usuario asignado' }
])

interface ModalAsnReassingProps {
  modalState: ModalReturnType<Asn>
  onSuccess?: () => void
}

export const ModalAsnReassing: React.FC<ModalAsnReassingProps> = ({
  modalState,
  onSuccess
}) => {
  const { callApi, state } = useApi<ApiResponse<AsnDetailLpn>>()
  const pagination = usePagination(5)
  const { isActive, message, isSuccess, openSnackBar } = useSnackbar()

  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [selectedLpnIds, setSelectedLpnIds] = useState<(string | number)[]>([])
  const [isSaving, setIsSaving] = useState(false)

  const asnId = modalState.extendedProps?.asnId

  const fetchLpns = (page: number, pageSize: number) => {
    if (!asnId) return
    callApi(
      apiCall(
        'GET',
        `${ApiConfig.AsnDetailApi}/GetAsnDetailLpn?pageIndex=${page}&pageSize=${pageSize}&asnId=${asnId}`
      )
    ).catch((error) => {
      openSnackBar((error as any)?.data?.message || 'Error al cargar LPNs', false)
    })
  }

  useEffect(() => {
    if (modalState.isOpen && asnId) {
      setSelectedUser(null)
      setSelectedLpnIds([])
      pagination.setPagination(0, pagination.pageSize)
      fetchLpns(0, pagination.pageSize)
    }
  }, [modalState.isOpen, asnId])

  const handlePagination = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    fetchLpns(auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }

  const handleSave = () => {
    if (!asnId || !selectedUser || selectedLpnIds.length === 0) return
    setIsSaving(true)
    apiCall('PUT', `${ApiConfig.AsnDetailApi}/${asnId}/user?userId=${selectedUser.userId}`, selectedLpnIds)
      .then(() => {
        openSnackBar('Usuario reasignado correctamente', true)
        onSuccess?.()
        modalState.closeModal()
      })
      .catch((error) => {
        openSnackBar((error as any)?.data?.message || 'Error al reasignar usuario', false)
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  return (
    <Fragment>
      <Modal
        open={modalState.isOpen}
        maxWidth={modalState.size}
        title={modalState.title}
        onClose={modalState.closeModal}
        cancelButton={{
          action: modalState.closeModal,
          title: 'Cancelar'
        }}
        successButton={{
          action: handleSave,
          title: 'Guardar',
          disabled: !selectedUser || selectedLpnIds.length === 0 || isSaving
        }}
      >
        {/* Banner informativo */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, backgroundColor: '#e8f4fd', border: '1px solid #90caf9', borderRadius: '6px', px: 1.5, py: 1, mb: 2 }}>
          <InfoIcon sx={{ color: '#1976d2', fontSize: 18, flexShrink: 0 }} />
          <Typography variant='body2' sx={{ color: '#1565c0' }}>
            Busca el nuevo usuario y luego selecciona las LPNs que deseas reasignarle.
          </Typography>
        </Box>

        {/* 1 · Nuevo usuario */}
        <Typography variant='overline' sx={{ fontWeight: 700, color: '#555', letterSpacing: 1 }}>
          1 · Nuevo usuario
        </Typography>
        <Box sx={{ mb: 0.5 }}>
          <AutoCompleterV3<User>
            config={userConfig}
            value={selectedUser}
            onChange={setSelectedUser}
            placeholder='Buscar usuario por nombre...'
            renderOption={(user) => (
              <div>
                <div style={{ fontWeight: 'bold' }}>{user.userAlias}</div>
                <div style={{ fontSize: '0.875rem', color: '#666' }}>{user.userName}</div>
              </div>
            )}
            renderValue={(user) => user.userAlias}
            width='100%'
          />
        </Box>
        <Typography variant='caption' sx={{ color: '#888', mb: 1.5, display: 'block' }}>
          Escribe 3 o más caracteres para buscar
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        {/* 2 · LPNs a reasignar */}
        <Typography variant='overline' sx={{ fontWeight: 700, color: '#555', letterSpacing: 1, mb: 1, display: 'block' }}>
          2 · LPNs a reasignar
        </Typography>

        <GenericTableV2<AsnDetailLpn>
          data={state.data?.items || []}
          columns={lpnColumns}
          keyField='lpn'
          loading={state.status === Status.Loading}
          emptyMessage='No hay LPNs para este ASN'
          selection={{
            selectedIds: selectedLpnIds,
            onSelectionChange: setSelectedLpnIds
          }}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            total: state.data?.totalItems || 0,
            onPageChange: handlePagination
          }}
          styling={{ dense: true }}
          
        />
      </Modal>

      <SnackBar
        message={message}
        status={isSuccess ? 'success' : 'error'}
        open={isActive}
      />
    </Fragment>
  )
}
